import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate'
import type { SupportedLanguage } from '@/i18n'
import { localizedRoutes } from '@/i18n/routes'
import { cn } from '@/lib/utils'

interface MachineCardProps {
  image: string
  imageAlt?: string
  name: string
  description: string
  slug: string
  category?: string
  /**
   * If true, renders a compact variant for tight home-page grids.
   */
  compact?: boolean
  className?: string
}

/**
 * MachineCard — industrial product card used on Home and Machines pages.
 *
 * Design: sharp-cornered (rounded-sm), white card, subtle border, brand-blue
 * accent on hover. Image sits in a fixed 4:3 frame with engineered
 * coordinate ticks in the corners. Hovering lifts the card by 2px and adds
 * an edge glow. No bouncy animations.
 */
export function MachineCard({
  image,
  imageAlt,
  name,
  description,
  slug,
  category,
  compact = false,
  className,
}: MachineCardProps) {
  const { t, i18n } = useTranslation()
  const navigate = useLocalizedNavigate()
  const currentLang = (i18n.language || 'it') as SupportedLanguage

  const machinesSegment = localizedRoutes.machines[currentLang]
  const href = `/${currentLang}/${machinesSegment}/${slug}`

  const handleNavigate = () => {
    navigate(`${machinesSegment}/${slug}`)
  }

  return (
    <article
      className={cn(
        'group relative flex h-full flex-col bg-white fx-card-border',
        'rounded-sm shadow-sm',
        'transition-[transform,box-shadow,border-color] duration-300 ease-out',
        'hover:-translate-y-0.5 hover:shadow-md hover:border-brand-400',
        className
      )}
    >
      {/* Image frame */}
      <div className="relative aspect-[4/3] overflow-hidden bg-ink-100">
        <img
          src={image}
          alt={imageAlt ?? name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
        />
        {/* Corner coordinate ticks */}
        <div aria-hidden="true" className="absolute top-2 left-2 flex gap-0.5">
          <span className="block h-2 w-px bg-brand-500" />
          <span className="block h-4 w-px bg-brand-500" />
        </div>
        <div aria-hidden="true" className="absolute top-2 right-2 flex gap-0.5">
          <span className="block h-4 w-px bg-brand-500" />
          <span className="block h-2 w-px bg-brand-500" />
        </div>

        {/* Category tag */}
        {category && (
          <span className="absolute bottom-3 left-3 rounded-sm bg-white/95 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-brand-700 shadow-sm">
            {category}
          </span>
        )}
      </div>

      {/* Body */}
      <div
        className={cn(
          'flex flex-1 flex-col',
          compact ? 'p-4' : 'p-5 md:p-6'
        )}
      >
        <h3
          className={cn(
            'font-bold text-ink-900 leading-snug',
            compact ? 'text-lg' : 'text-xl md:text-[1.375rem]'
          )}
        >
          <a
            href={href}
            onClick={(e) => {
              e.preventDefault()
              handleNavigate()
            }}
            className="focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
          >
            <span className="absolute inset-0" aria-hidden="true" />
            {name}
          </a>
        </h3>

        <p
          className={cn(
            'mt-2 fx-text-pretty text-ink-600',
            compact ? 'text-sm' : 'text-[15px]',
            'line-clamp-3'
          )}
        >
          {description}
        </p>

        <div className="mt-auto flex items-center justify-between border-t border-ink-200/70 pt-4">
          <span
            aria-hidden="true"
            className="inline-flex items-center gap-2 text-sm font-semibold text-brand-600 transition-colors group-hover:text-brand-700"
          >
            {t('buttons.learnMore')}
            <ArrowRight
              className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
              strokeWidth={2.5}
            />
          </span>
        </div>
      </div>
    </article>
  )
}
